<template>
	<div>
		<AppLoading v-if="lojaReq.carregando" />
		<template v-else-if="lojaReq.erro"> {{ lojaReq.erro }} </template>
		<template v-else-if="loja">
			<ComponentAuthGuard :requires="['admin', adminLojaGuard]">
				<v-btn
					fixed
					dark
					fab
					bottom
					right
					:style="{bottom: `${$vuetify.application.bottom + 10}px`}"
					color="primary"
					@click="() => $router.push({
						name: 'editar-loja',
						params: { id },
					})"
				>
					<v-icon>mdi-pencil</v-icon>
				</v-btn>
			</ComponentAuthGuard>
			<v-container key="cabecalho">
				<v-row class="mb-2" justify="center">
					<v-avatar size="90">
						<img
							v-if="loja.urlLogo"
							:src="loja.urlLogo"
						/>
						<v-icon
							v-else
							v-text="'mdi-bank'"
							size="90"
						/>
					</v-avatar>
				</v-row>
				<v-row justify="center" class="font-weight-bold">
					<div>
						{{ loja.nome }} &mdash;&nbsp;
						<router-link :to="{
							name: 'detalhar-potencia',
							params: {
								id: loja.potencia._id,
							}
						}"> {{ loja.potencia.nome }} </router-link>
					</div>
				</v-row>
				<template v-if="loja.periodoDaSessao && loja.horarioDaSessao">
					<v-row justify="center">
						Reuniões
					</v-row>
					<v-row justify="center">
						{{ loja.periodoDaSessao }} às {{ loja.horarioDaSessao }}
					</v-row>
				</template>
				<v-row justify="center" v-if="loja.endereco">
					<v-dialog max-width="680" v-model="dialogoEndereco">
						<template #activator="{ on }">
							<v-btn text color="primary" v-on="on">
								Endereço
								<v-icon> mdi-map-marker </v-icon>
							</v-btn>
						</template>
						<v-card style="position: relative;" height="500">
							<AppLoading
								v-if="iframeIsLoading"
								style="position: absolute; top: 0; right: 0; left: 0; bottom: 0;"
								class="d-flex align-center justify-center"
							/>
							<iframe
								v-show="!iframeIsLoading"
								:src="`https://www.google.com/maps/embed/v1/place?q=${loja.endereco}&key=AIzaSyDttAS5QLL4vm8KKGfAMp6lCvMdRcJkr7Y`"
								frameborder="0"
								allowfullscreen=""
								aria-hidden="false"
								tabindex="0"
								height="100%"
								width="100%"
								@load="() => iframeIsLoading = false"
							/>
							<v-btn
								fab
								small
								outlined
								color="grey"
								style="position: absolute; top: 0; right: 0;"
								class="ma-1"
								@click="() => dialogoEndereco = false"
							>
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</v-card>
					</v-dialog>
				</v-row>
				<CardsDeTotaisPorGrau
					v-if="loja.totaisPorGrau"
					justify="center"
					class="mt-4"
					:totais="loja.totaisPorGrau"
				/>
			</v-container>
			<v-card>
				<v-tabs
					key="tabs"
					v-model="tab"
					fixed-tabs
					background-color="primary"
					dark
				>
					<v-tab v-for="tab in tabs" :key="tab">
						{{ tab }}
					</v-tab>
					<!-- <v-tab-item key="Geral">
					</v-tab-item> -->
					<v-tab-item key="Irmãos">
						<v-list-item>
							<SeletorDeSituacaoDoMacon
								label="Situação"
								v-model="situacaoDoMacon"
								hide-details
							/>
						</v-list-item>
						<AppLoading v-if="irmaosReq.carregando" />
						<template v-else-if="irmaosReq.erro"> {{ irmaosReq.erro }} </template>
						<template v-else>
							<v-list-item
								v-for="irmao in irmaosOrdenados"
								:key="irmao._id"
								@click="() => $router.push({ name: 'detalhar-irmao', params: { id: irmao._id } })"
								v-bind="{
									[obterCargoDeAdminDaLoja(irmao, id) || irmao.cargoDeAdminDaPotencia ?	'three-line' : 'two-line']: true
								}"
							>
								<v-list-item-avatar>
									<v-img
										v-if="irmao.foto"
										:src="irmao.foto"
										style="object-fit: cover"
									>
										<template v-slot:placeholder>
											<v-progress-circular indeterminate color="grey"></v-progress-circular>
										</template>
									</v-img>
									<v-icon
										v-else
										v-text="'mdi-account'"
									/>
								</v-list-item-avatar>
								<v-list-item-content>
									<v-list-item-subtitle>
										{{ [
											obterCargoDeAdminDaLoja(irmao, id),
											irmao.cargoDeAdminDaPotencia && `${irmao.cargoDeAdminDaPotencia} (${loja && loja.potencia.nome})`
										]
										.filter(cargo => !!cargo)
										.join(' / ') }}
									</v-list-item-subtitle>
									<v-list-item-title>
										{{ irmao.nomeCompleto }}
									</v-list-item-title>
									<v-list-item-subtitle
										class="d-flex align-center"
										:class="[ irmao.jaLogou ? 'success--text' : 'error--text' ]"
									>
										<div> Atualizado em {{ formatarData(irmao.ultimaAtualizacao) }} </div>
									</v-list-item-subtitle>
								</v-list-item-content>
							</v-list-item>
						</template>
					</v-tab-item>
				</v-tabs>
			</v-card>
		</template>
		<div style="height:56px;" />
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import LojaService from '@/services/LojaService'
import { Loja } from '../../../common/types/Loja'
import ItemListaDetalhes from '@/components/ItemListaDetalhes.vue'
import Irmao, { SituacaoDoMacon } from '../../../common/types/Irmao'
import ComponentAuthGuard from '@/components/ComponentAuthGuard.vue'
import SeletorDeSituacaoDoMacon from '@/components/SeletorDeSituacaoDoMacon.vue'
import { adminLojaGuard as guardaDeAdminDaLoja } from '../../shareds/sharedsLoja'
import { formatarData } from '@/shareds/formatadores'
import { obterCargoDeAdminDaLoja, ordenarIrmaosPorCargo } from './../../../common/shareds/sharedsIrmao'
import CardsDeTotaisPorGrau from '@/components/CardsDeTotaisPorGrau.vue'
import { getErrorMessage } from '@/helpers/error-helpers'

const props = defineProps({
	id: { type: String, required: true },
})

const iframeIsLoading = ref(true)
const dialogoEndereco = ref(false)

const loja = ref<Loja | null>(null)
const irmaos = ref<Irmao[]>([])
const situacaoDoMacon = ref<SituacaoDoMacon>('Regular')

const tab = ref(0)
const tabs = [
	// 'Geral',
	'Irmãos',
]

const lojaReq = ref({
	carregando: true,
	erro: '',
})

const irmaosReq = ref({
	carregando: true,
	erro: '',
})

const adminLojaGuard = computed(() => guardaDeAdminDaLoja(props.id))

const irmaosOrdenados = computed(() => ordenarIrmaosPorCargo(irmaos.value, props.id))

async function buscarLoja() {
	lojaReq.value = {
		carregando: true,
		erro: '',
	}
	try {
		loja.value = await LojaService.get(props.id)
	} catch (error) {
		lojaReq.value.erro = getErrorMessage(error)
	} finally {
		lojaReq.value.carregando = false
	}
}

async function buscarIrmaos() {
	irmaosReq.value = {
		carregando: true,
		erro: '',
	}
	try {
		irmaos.value = await LojaService.getIrmaos(props.id, {
			situacaoDoMacon: situacaoDoMacon.value,
		})
	} catch (error) {
		irmaosReq.value.erro = getErrorMessage(error)
	} finally {
		irmaosReq.value.carregando = false
	}
}

watch(situacaoDoMacon, buscarIrmaos)

buscarLoja()
buscarIrmaos()
</script>
